import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ContactModal from "../../components/ContactModal";
import ContactTimeline from "../../components/ContactTimeline";
import NewTicketModal from "../../components/NewTicketModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  headerCard: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  avatar: {
    width: 72,
    height: 72,
  },
  info: {
    flex: 1,
    minWidth: 200,
  },
  actions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  extraInfo: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  timelinePaper: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
}));

const ContactProfile = () => {
  const classes = useStyles();
  const history = useHistory();
  const { contactId } = useParams();

  const [contact, setContact] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: contactData }, { data: timelineData }] = await Promise.all([
        api.get(`/contacts/${contactId}`),
        api.get(`/contacts/${contactId}/timeline`),
      ]);
      setContact(contactData);
      setEvents(timelineData);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const handleCloseTicketModal = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  return (
    <MainContainer>
      <ContactModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          loadData();
        }}
        contactId={contactId ? Number(contactId) : null}
      />
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contact || {}}
        onClose={handleCloseTicketModal}
      />
      <MainHeader>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <IconButton
            size="small"
            onClick={() => history.push("/contacts")}
            title="Voltar"
          >
            <ArrowBackIcon />
          </IconButton>
          <Title>Ficha do Contato</Title>
        </div>
        <MainHeaderButtonsWrapper />
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <div className={classes.centered}>
            <CircularProgress />
          </div>
        ) : !contact ? (
          <Typography color="textSecondary" style={{ padding: 16 }}>
            Contato não encontrado.
          </Typography>
        ) : (
          <>
            <Paper variant="outlined" className={classes.headerCard}>
              <Avatar
                src={contact.profilePicUrl}
                alt={contact.name}
                className={classes.avatar}
              />
              <div className={classes.info}>
                <Typography variant="h6">{contact.name}</Typography>
                {contact.number && (
                  <Typography variant="body2" color="textSecondary">
                    <Link href={`tel:${contact.number}`}>{contact.number}</Link>
                  </Typography>
                )}
                {contact.email && (
                  <Typography variant="body2" color="textSecondary">
                    <Link href={`mailto:${contact.email}`}>{contact.email}</Link>
                  </Typography>
                )}
              </div>
              <div className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => setNewTicketModalOpen(true)}
                >
                  Conversar
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setModalOpen(true)}
                >
                  Editar
                </Button>
              </div>
            </Paper>

            {contact.extraInfo && contact.extraInfo.length > 0 && (
              <Paper variant="outlined" className={classes.extraInfo}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Informações adicionais
                </Typography>
                <Grid container spacing={2}>
                  {contact.extraInfo.map((info) => (
                    <Grid item xs={12} sm={6} md={4} key={info.id}>
                      <Typography variant="caption" color="textSecondary">
                        {info.name}
                      </Typography>
                      <Typography variant="body2">{info.value}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            <Paper variant="outlined" className={classes.timelinePaper}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Histórico
              </Typography>
              <Divider style={{ marginBottom: 16 }} />
              <ContactTimeline events={events} />
            </Paper>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default ContactProfile;
